import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import { useMemo } from "react";
import ButtonWithLoading from "@/components/button-with-loading";
import DepartmentInput from "components/department/input";
import HeaderPortal from "components/portal/header-portal";
import { Form } from "react-crud-form";
import { FormLabel } from "components/form";
import GradeInput from "components/grade/input";
import { Input } from "ui/components/input";
import { PermissionControlledInput } from "permissions/components/input";
import { PermissionGate } from "permissions/components/gate";
import React from "react";
import { budgetAllocationHourlySchema, budgetAllocationStandardSchema } from "./schema";
import { useCreateBudgetAllocation } from "@/modules/work/jobs/allocation/crud/create/mutation";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { getRequiredFields } from "components/form/utils";

const AllocationForm = ({ handleClose, type }) => {
  const navigate = useNavigate();
  const { jobId } = useParams();

  const createBudgetMutation = useCreateBudgetAllocation();

  // Hourly and bucket jobs allocate by percentage; others allocate by hours.
  const isHourlyOrBucket = ["hourly", "bucket"].includes(type);

  const schema = useMemo(() => isHourlyOrBucket ? budgetAllocationHourlySchema : budgetAllocationStandardSchema, [isHourlyOrBucket]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      job: jobId,
      department: "",
      grade: "",
      hours: "",
      percentage: ""
    },
  });

  const requiredFields = useMemo(() => getRequiredFields(schema), [schema]);

  async function handleCreate(context) {
    const values = { ...context.values, job: jobId };
    const response = await createBudgetMutation.mutateAsync({ jobId, values });
    navigate(-1);
  }

  return (
    <Form
      form={form}
      create={handleCreate}
      itemId={jobId || "+"}
      schema={schema}
    >
      {({ isCreating, createItem, isNewItem, isDirty }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              setIsNew(false);
              createItem().then(handleClose);
            }
          },
          { preventDefault: true },
        );

        useHotkeys(
          "ctrl+shift+s, cmd+shift+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              setIsNew(true);
              createItem().then(() => {
                if (!isNew) {
                  handleClose();
                }
              });
            }
          },
          { preventDefault: true },
        );
        return (
          <div className="space-y-6 max-w-md">
            <FormField
              control={form.control}
              name="department"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    type="form"
                    collection="allocated_hours"
                    rule={"create:PC,SC,JC,AM"}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>Department</FormLabel>

                    <DepartmentInput {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="grade"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    type="form"
                    collection="allocated_hours"
                    rule={"create:PC,SC,JC,AM"}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>Grade</FormLabel>

                    <GradeInput {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!isHourlyOrBucket && (
              <FormField
                control={form.control}
                name="hours"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      type="form"
                      collection="allocated_hours"
                      rule={"create:PC,SC,JC,AM"}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>Hours</FormLabel>

                      <Input {...field} type="number" placeholder="e.g. 160" />
                    </PermissionControlledInput>
                    <FormDescription>
                      Enter the total hours allocated to this department (e.g. 160).
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {isHourlyOrBucket && (
              <FormField
                control={form.control}
                name="percentage"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      type="form"
                      collection="allocated_hours"
                      rule={"create:PC,SC,JC,AM"}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>Percentage</FormLabel>

                      <Input {...field} type="number" placeholder="e.g. 40" />
                    </PermissionControlledInput>
                    <FormDescription>
                      Enter the percentage of expected billable work allocated to this department (e.g. 40).
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <HeaderPortal>
              <PermissionGate
                collection="allocated_hours"
                rule={"create:PC,SC,JC,AM"}
                fallback={
                  <div className="text-sm text-gray-500 italic">
                    You don't have permission to create budget in this job
                  </div>
                }
              >
                <ButtonWithLoading
                  variant="default"
                  isLoading={isCreating}
                  onClick={createItem}
                  disabled={!isDirty}
                >
                  Create
                </ButtonWithLoading>
              </PermissionGate>
            </HeaderPortal>
          </div>
        );
      }}
    </Form>
  );
};

export default AllocationForm;
